/**
 * Dashboard-related Supabase queries
 * Pure async functions - no React, no useQuery
 */

import { supabase } from "@/integrations/supabase/client";
import { startOfWeek, endOfWeek, subWeeks, format, startOfMonth, endOfMonth, subMonths, subDays, differenceInDays, parseISO, getDay } from 'date-fns';
import { getStateSalesTaxRate } from '@/lib/stateSalesTaxRates';
import {
  determineStatus,
  generateRecommendation,
  INVENTORY_SPEND_TARGET_PERCENT,
  PAYROLL_TAX_RATE,
} from '@/lib/dashboardInsightsHelpers';
import { getShiftPeriod, SHIFT_LABELS } from '@/lib/shiftUtils';
import { PLATFORMS } from '@/lib/platformConstants';
import type {
  DashboardInsights,
  MacroKPIs,
  ExpenseRatios,
  StatusLevel,
  PriorityDecision,
  TaxLiabilities,
  MonthlyExpense,
  PerformanceSnapshot,
  SpendingBreakdown,
  PlatformSales,
  ShiftPerformance,
} from '@/types/dashboard';

export interface DashboardDateRange {
  startDate: string;
  endDate: string;
}

const round2 = (v: number): number => Math.round(v * 100) / 100;

/**
 * Deduplicate expenses between bank_transactions and bank_statements.
 * If bank_transactions have data for a given period, prefer them over bank_statements
 * to avoid double-counting when both Teller and CSV data exist.
 */
function deduplicateExpenses(
  txExpenses: { amount: number; subcategory?: string | null }[],
  stmtExpenses: { amount: number; bucket?: string | null }[],
  mapStmtToSubcategory: (row: { bucket?: string | null }) => string
): { amount: number; subcategory?: string | null }[] {
  if (txExpenses.length > 0 && stmtExpenses.length > 0) {
    const txSubcategories = new Set(
      txExpenses.map(e => (e.subcategory || '').toLowerCase())
    );
    const deduplicatedStmts = stmtExpenses.filter(row => {
      const mappedSub = mapStmtToSubcategory(row);
      return !txSubcategories.has(mappedSub);
    });
    return [
      ...txExpenses,
      ...deduplicatedStmts.map(row => ({
        amount: -Math.abs(Number(row.amount)),
        subcategory: mapStmtToSubcategory(row),
      })),
    ];
  }

  if (txExpenses.length > 0) return txExpenses;

  return stmtExpenses.map(row => ({
    amount: -Math.abs(Number(row.amount)),
    subcategory: mapStmtToSubcategory(row),
  }));
}

/**
 * Fetch all dashboard insights for a company.
 * When dateRange is provided, uses that as the "current" period and computes a same-length "previous" period immediately before it.
 * When not provided, uses this week (Mon–Sun) and previous week.
 */
export async function fetchDashboardInsights(
  companyId: string,
  dateRange?: DashboardDateRange | null
): Promise<DashboardInsights> {
  const now = new Date();
  let currentStart: Date;
  let currentEnd: Date;
  let previousStart: Date;
  let previousEnd: Date;

  if (dateRange?.startDate && dateRange?.endDate) {
    currentStart = parseISO(dateRange.startDate);
    currentEnd = parseISO(dateRange.endDate);
    const periodDays = differenceInDays(currentEnd, currentStart) + 1;
    previousEnd = subDays(currentStart, 1);
    previousStart = subDays(previousEnd, periodDays - 1);
  } else {
    const currentWeekStart = startOfWeek(now, { weekStartsOn: 1 });
    const currentWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
    currentStart = currentWeekStart;
    currentEnd = currentWeekEnd;
    previousStart = subWeeks(currentWeekStart, 1);
    previousEnd = subWeeks(currentWeekEnd, 1);
  }

  const currentStartStr = format(currentStart, 'yyyy-MM-dd');
  const currentEndStr = format(currentEnd, 'yyyy-MM-dd');
  const previousStartStr = format(previousStart, 'yyyy-MM-dd');
  const previousEndStr = format(previousEnd, 'yyyy-MM-dd');

  const currentMonthStart = startOfMonth(now);
  const currentMonthEnd = endOfMonth(now);
  const recurringStart = format(subMonths(currentMonthStart, 3), 'yyyy-MM-dd');

  const [
    currentWeekSalesResult,
    previousWeekSalesResult,
    currentWeekExpensesResult,
    previousWeekExpensesResult,
    currentWeekExpensesStmtResult,
    previousWeekExpensesStmtResult,
    bankAccountsResult,
    tellerBalancesResult,
    companyResult,
    currentMonthSalesResult,
    currentMonthPayrollResult,
    recurringExpensesResult,
    recurringExpensesStmtResult,
    debtServiceResult,
    incomeTaxResult,
    depositsResult,
    rentExpensesTxResult,
  ] = await Promise.all([
    supabase
      .from('sales_transactions')
      .select('platform, gross_sales, net_payout_amount, tip_amount, transaction_fees, uber_commission, commission, sale_date, sale_time, subtotal_before_discounts, total_payments_amount, merchant_promotions, uber_promotion_credit, miscellaneous_deductions, total_payout_amount, promotions_on_delivery, marketing_fees, net_deposit_amount, price_adjustments')
      .eq('company_id', companyId)
      .gte('sale_date', currentStartStr)
      .lte('sale_date', currentEndStr),
    
    supabase
      .from('sales_transactions')
      .select('platform, gross_sales, net_payout_amount, tip_amount, subtotal_before_discounts, total_payout_amount, net_deposit_amount, merchant_promotions, price_adjustments')
      .eq('company_id', companyId)
      .gte('sale_date', previousStartStr)
      .lte('sale_date', previousEndStr),
    
    supabase
      .from('bank_transactions')
      .select('amount, category_type, subcategory')
      .eq('company_id', companyId)
      .eq('category_type', 'expenses')
      .gte('transaction_date', currentStartStr)
      .lte('transaction_date', currentEndStr),
    
    supabase
      .from('bank_transactions')
      .select('amount, category_type, subcategory')
      .eq('company_id', companyId)
      .eq('category_type', 'expenses')
      .gte('transaction_date', previousStartStr)
      .lte('transaction_date', previousEndStr),
    
    supabase
      .from('bank_statements')
      .select('amount, category, bucket')
      .eq('company_id', companyId)
      .eq('category', 'expenses')
      .gte('transaction_date', currentStartStr)
      .lte('transaction_date', currentEndStr),
    
    supabase
      .from('bank_statements')
      .select('amount, category, bucket')
      .eq('company_id', companyId)
      .eq('category', 'expenses')
      .gte('transaction_date', previousStartStr)
      .lte('transaction_date', previousEndStr),
    
    supabase
      .from('bank_accounts')
      .select('*')
      .eq('company_id', companyId)
      .eq('is_active', true),

    supabase.functions.invoke<{ balances?: Record<string, number> }>('teller-refresh-balances', { body: {} })
      .catch(() => ({ data: null, error: new Error('Teller balance refresh failed') })),

    supabase
      .from('companies')
      .select('state')
      .eq('id', companyId)
      .single(),

    supabase
      .from('sales_transactions')
      .select('gross_sales')
      .eq('company_id', companyId)
      .gte('sale_date', format(currentMonthStart, 'yyyy-MM-dd'))
      .lte('sale_date', format(currentMonthEnd, 'yyyy-MM-dd')),

    supabase
      .from('payroll_transactions')
      .select('gross_wages')
      .eq('company_id', companyId)
      .gte('transaction_date', format(currentMonthStart, 'yyyy-MM-dd'))
      .lte('transaction_date', format(currentMonthEnd, 'yyyy-MM-dd')),

    supabase
      .from('bank_transactions')
      .select('amount, subcategory, transaction_date')
      .eq('company_id', companyId)
      .eq('category_type', 'expenses')
      .gte('transaction_date', recurringStart)
      .lte('transaction_date', format(currentMonthEnd, 'yyyy-MM-dd')),

    supabase
      .from('bank_statements')
      .select('amount, bucket, transaction_date')
      .eq('company_id', companyId)
      .eq('category', 'expenses')
      .gte('transaction_date', recurringStart)
      .lte('transaction_date', format(currentMonthEnd, 'yyyy-MM-dd')),

    supabase
      .from('bank_transactions')
      .select('amount')
      .eq('company_id', companyId)
      .eq('category_type', 'balance_sheet')
      .eq('detail_source', 'loan_payments')
      .gte('transaction_date', currentStartStr)
      .lte('transaction_date', currentEndStr),

    supabase
      .from('bank_transactions')
      .select('amount')
      .eq('company_id', companyId)
      .eq('category_type', 'balance_sheet')
      .eq('detail_source', 'tax_outflows')
      .gte('transaction_date', currentStartStr)
      .lte('transaction_date', currentEndStr),

    supabase
      .from('bank_transactions')
      .select('amount')
      .eq('company_id', companyId)
      .eq('category_type', 'revenue')
      .eq('subcategory', 'deposits')
      .gte('transaction_date', currentStartStr)
      .lte('transaction_date', currentEndStr),

    supabase
      .from('bank_transactions')
      .select('amount, subcategory')
      .eq('company_id', companyId)
      .eq('category_type', 'expenses')
      .eq('subcategory', 'rent')
      .gte('transaction_date', currentStartStr)
      .lte('transaction_date', currentEndStr),
  ]);

  const currentWeekSales = currentWeekSalesResult.data || [];
  const previousWeekSales = previousWeekSalesResult.data || [];
  const currentWeekExpensesTx = currentWeekExpensesResult.data || [];
  const previousWeekExpensesTx = previousWeekExpensesResult.data || [];
  const currentWeekExpensesStmt = currentWeekExpensesStmtResult.data || [];
  const previousWeekExpensesStmt = previousWeekExpensesStmtResult.data || [];

  const mapStmtToSubcategory = (row: { bucket?: string | null }) => {
    const b = (row.bucket || '').toLowerCase();
    if (b === 'payroll') return 'payroll';
    if (b === 'cogs' || b === 'food') return 'cogs';
    if (b === 'rent') return 'rent';
    if (b === 'opex') return 'opex';
    return 'other';
  };

  const currentWeekExpenses = deduplicateExpenses(
    currentWeekExpensesTx,
    currentWeekExpensesStmt,
    mapStmtToSubcategory
  );
  const previousWeekExpenses = deduplicateExpenses(
    previousWeekExpensesTx,
    previousWeekExpensesStmt,
    mapStmtToSubcategory
  );

  const bankAccounts = bankAccountsResult.data || [];
  const liveTellerBalances = (tellerBalancesResult as { data?: { balances?: Record<string, number> } | null })?.data?.balances ?? {};
  const company = companyResult.data;
  const currentMonthSales = currentMonthSalesResult.data || [];
  const currentMonthPayroll = currentMonthPayrollResult.data || [];
  const recurringExpensesTx = recurringExpensesResult.data || [];
  const recurringExpensesStmt = recurringExpensesStmtResult.data || [];
  const debtServiceTxns = debtServiceResult.data || [];
  const incomeTaxTxns = incomeTaxResult.data || [];
  const depositsTxns = depositsResult.data || [];
  const rentExpensesTx = rentExpensesTxResult.data || [];

  const recurringExpenses = deduplicateExpenses(
    recurringExpensesTx.map(tx => ({ ...tx, subcategory: tx.subcategory })),
    recurringExpensesStmt,
    mapStmtToSubcategory
  ).map((e, i) => ({
    ...e,
    transaction_date: i < recurringExpensesTx.length
      ? recurringExpensesTx[i]?.transaction_date
      : recurringExpensesStmt[i - recurringExpensesTx.length]?.transaction_date,
  }));

  // Tax liabilities calculation
  const stateCode = (company as { state?: string } | null)?.state ?? null;
  const salesTaxRate = getStateSalesTaxRate(stateCode);
  const currentMonthNetSales = currentMonthSales.reduce((sum, t) => {
    const net = Number((t as { net_sale_amount_paid_by_customer?: number })?.net_sale_amount_paid_by_customer) ||
      Number((t as { total_net_sales?: number })?.total_net_sales) ||
      Number((t as { gross_sales?: number })?.gross_sales) || 0;
    return sum + net;
  }, 0);
  const totalW2PayrollCurrentMonth = currentMonthPayroll.reduce((sum, p) => sum + (Number(p.gross_wages) || 0), 0);
  const salesTaxLiability = salesTaxRate * currentMonthNetSales;
  const payrollTaxLiability = PAYROLL_TAX_RATE * totalW2PayrollCurrentMonth;

  const taxLiabilities: TaxLiabilities = {
    salesTaxLiability,
    payrollTaxLiability,
    salesTaxRatePercent: salesTaxRate * 100,
    stateCode,
  };

  // ── Per-platform KPI calculations (KPI Map v1.0) ──
  const toastTxns = currentWeekSales.filter(t => t.platform === PLATFORMS.TOAST);
  const uberTxns = currentWeekSales.filter(t => t.platform === PLATFORMS.UBER);
  const doorDashTxns = currentWeekSales.filter(t => t.platform === PLATFORMS.DOORDASH);

  // Toast: Amount column is incl. tax → divide by (1 + taxRate)
  const toastGrossSalesInclTax = toastTxns.reduce((s, t) => s + (Number(t.subtotal_before_discounts) || Number(t.gross_sales) || 0), 0);
  const toastGrossSalesExclTax = toastGrossSalesInclTax / (1 + salesTaxRate);
  const toastSalesTax = toastGrossSalesInclTax - toastGrossSalesExclTax;
  const toastTips = toastTxns.reduce((s, t) => s + (Number(t.tip_amount) || 0), 0);
  const toastTickets = toastTxns.filter(t => (Number(t.subtotal_before_discounts) || Number(t.gross_sales) || 0) > 0).length;
  const toastATP = toastTickets > 0 ? toastGrossSalesExclTax / toastTickets : 0;
  const toastTransactionFees = toastTxns.reduce((s, t) => s + (Number(t.transaction_fees) || 0), 0);
  const toastNetPayouts = toastTxns.reduce((s, t) => s + (Number(t.net_payout_amount) || 0), 0);
  const toastPayoutRatio = toastGrossSalesExclTax > 0
    ? (toastNetPayouts - toastSalesTax - toastTips) / toastGrossSalesExclTax : 0;

  // Uber: gross_sales is already excl. tax; promotions stored as positive (abs applied at ingestion)
  const uberGrossSales = uberTxns.reduce((s, t) => s + (Number(t.gross_sales) || 0), 0);
  const uberDeductions = uberTxns.reduce((s, t) => s + Math.abs(Number(t.merchant_promotions) || 0) + Math.abs(Number(t.price_adjustments) || 0), 0);
  const uberNetSales = uberGrossSales - uberDeductions;
  const uberSalesTax = uberNetSales * salesTaxRate;
  const uberTips = uberTxns.reduce((s, t) => s + (Number(t.tip_amount) || 0), 0);
  const uberTickets = uberTxns.filter(t => (Number(t.gross_sales) || 0) > 0).length;
  const uberATP = uberTickets > 0 ? uberNetSales / uberTickets : 0;
  const uberCoFunding = uberTxns.reduce((s, t) => s + Math.abs(Number(t.uber_promotion_credit) || 0), 0);
  const uberCommissions = uberTxns.reduce((s, t) => s + Math.abs(Number(t.uber_commission) || 0), 0);
  const uberCommissionPct = uberGrossSales > 0 ? (uberCommissions / uberGrossSales) * 100 : 0;
  const uberAdSpend = uberTxns.reduce((s, t) => s + Math.abs(Number(t.miscellaneous_deductions) || 0), 0);
  const uberNetPayouts = uberTxns.reduce((s, t) => s + (Number(t.total_payout_amount) || 0), 0);
  const uberPayoutRatio = uberGrossSales > 0
    ? (uberNetPayouts - uberSalesTax - uberTips) / uberGrossSales : 0;

  // DoorDash: use subtotal_before_discounts || gross_sales for correct gross; excl. tax
  const ddGrossSales = doorDashTxns.reduce((s, t) => s + (Number(t.subtotal_before_discounts) || Number(t.gross_sales) || 0), 0);
  const ddDeductions = doorDashTxns.reduce((s, t) => s + Math.abs(Number(t.merchant_promotions) || 0) + Math.abs(Number(t.price_adjustments) || 0), 0);
  const ddNetSales = ddGrossSales - ddDeductions;
  const ddSalesTax = ddNetSales * salesTaxRate;
  const ddTips = doorDashTxns.reduce((s, t) => s + (Number(t.tip_amount) || 0), 0);
  const ddTickets = doorDashTxns.filter(t => (Number(t.subtotal_before_discounts) || Number(t.gross_sales) || 0) > 0).length;
  const ddATP = ddTickets > 0 ? ddNetSales / ddTickets : 0;
  const ddCoFunding = doorDashTxns.reduce((s, t) => s + Math.abs(Number(t.promotions_on_delivery) || 0), 0);
  const ddCommissions = doorDashTxns.reduce((s, t) => s + Math.abs(Number(t.commission) || 0), 0);
  const ddCommissionPct = ddNetSales > 0 ? (ddCommissions / ddNetSales) * 100 : 0;
  const ddAdSpend = doorDashTxns.reduce((s, t) => s + Math.abs(Number(t.marketing_fees) || 0), 0);
  const ddNetPayouts = doorDashTxns.reduce((s, t) => s + (Number(t.net_deposit_amount) || Number(t.net_payout_amount) || 0), 0);
  const ddPayoutRatio = ddGrossSales > 0
    ? (ddNetPayouts - ddSalesTax - ddTips) / ddGrossSales : 0;

  // ── Aggregate KPIs across all platforms ──
  const currentGrossSalesExclTax = toastGrossSalesExclTax + uberGrossSales + ddGrossSales;
  const currentNetSalesExclTax = toastGrossSalesExclTax + uberNetSales + ddNetSales;
  const totalTips = toastTips + uberTips + ddTips;
  const totalSalesTaxCollected = toastSalesTax + uberSalesTax + ddSalesTax;
  const totalTickets = toastTickets + uberTickets + ddTickets;
  const overallATP = totalTickets > 0 ? currentNetSalesExclTax / totalTickets : 0;
  const totalNetPayouts = toastNetPayouts + uberNetPayouts + ddNetPayouts;

  // Legacy aliases for backward-compat in downstream calcs
  const currentGrossSales = currentGrossSalesExclTax;
  const currentNetPayout = totalNetPayouts;

  // Previous period metrics (same platform-aware logic)
  const prevToast = previousWeekSales.filter(t => t.platform === PLATFORMS.TOAST);
  const prevUber = previousWeekSales.filter(t => t.platform === PLATFORMS.UBER);
  const prevDD = previousWeekSales.filter(t => t.platform === PLATFORMS.DOORDASH);
  const prevToastGross = prevToast.reduce((s, t) => s + ((Number(t.subtotal_before_discounts) || Number(t.gross_sales) || 0) / (1 + salesTaxRate)), 0);
  const prevUberGross = prevUber.reduce((s, t) => s + (Number(t.gross_sales) || 0), 0);
  const prevDDGross = prevDD.reduce((s, t) => s + (Number(t.subtotal_before_discounts) || Number(t.gross_sales) || 0), 0);
  const previousGrossSales = prevToastGross + prevUberGross + prevDDGross;
  const prevUberDeductions = prevUber.reduce((s, t) => s + Math.abs(Number(t.merchant_promotions) || 0) + Math.abs(Number(t.price_adjustments) || 0), 0);
  const prevDDDeductions = prevDD.reduce((s, t) => s + Math.abs(Number(t.merchant_promotions) || 0) + Math.abs(Number(t.price_adjustments) || 0), 0);
  const previousNetPayout = previousWeekSales.reduce((s, t) => s + (Number(t.net_payout_amount) || Number(t.total_payout_amount) || Number(t.net_deposit_amount) || 0), 0);
  const previousWeekNetSales = prevToastGross + (prevUberGross - prevUberDeductions) + (prevDDGross - prevDDDeductions);

  // Expense breakdowns - always use Math.abs for consistent handling
  const laborExpenses = currentWeekExpenses
    .filter(e => e.subcategory?.toLowerCase() === 'payroll' || e.subcategory?.toLowerCase() === 'labor')
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
  
  const foodExpenses = currentWeekExpenses
    .filter(e => 
      e.subcategory?.toLowerCase() === 'cogs' || 
      e.subcategory?.toLowerCase() === 'food_cost' ||
      e.subcategory?.toLowerCase() === 'food'
    )
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);

  const rentExpenses = rentExpensesTx.reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
  
  const opexExpenses = currentWeekExpenses
    .filter(e => 
      e.subcategory?.toLowerCase() === 'opex' || 
      e.subcategory?.toLowerCase() === 'utilities'
    )
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
  
  const nonBusinessExpenses = currentWeekExpenses
    .filter(e => 
      e.subcategory?.toLowerCase() === 'non_business' || 
      e.subcategory?.toLowerCase() === 'personal'
    )
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);

  // Previous week expenses
  const prevLaborExpenses = previousWeekExpenses
    .filter(e => e.subcategory?.toLowerCase() === 'payroll' || e.subcategory?.toLowerCase() === 'labor')
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
  
  const prevFoodExpenses = previousWeekExpenses
    .filter(e => 
      e.subcategory?.toLowerCase() === 'cogs' || 
      e.subcategory?.toLowerCase() === 'food_cost' ||
      e.subcategory?.toLowerCase() === 'food'
    )
    .reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);

  // Percentages (use excl. tax gross sales as denominator)
  const salesDenominator = currentGrossSalesExclTax || 1;
  const laborPercent = (laborExpenses / salesDenominator) * 100;
  const foodPercent = (foodExpenses / salesDenominator) * 100;
  const opexPercent = (opexExpenses / salesDenominator) * 100;
  const nonBusinessPercent = (nonBusinessExpenses / salesDenominator) * 100;

  const prevLaborPercent = previousGrossSales > 0 ? (prevLaborExpenses / previousGrossSales) * 100 : 0;
  const prevFoodPercent = previousGrossSales > 0 ? (prevFoodExpenses / previousGrossSales) * 100 : 0;

  // Status determination
  const laborStatus = determineStatus(laborPercent, 30, prevLaborPercent);
  const foodStatus = determineStatus(foodPercent, 30, prevFoodPercent);
  
  // Inventory Spend Target
  const inventorySpendTarget = INVENTORY_SPEND_TARGET_PERCENT * previousWeekNetSales;
  const avgWeeklyInventory = foodExpenses;
  const inventoryStatus = determineStatus(avgWeeklyInventory, inventorySpendTarget || 1, prevFoodExpenses);

  // Priority decisions
  const priorityDecisions: PriorityDecision[] = [
    {
      id: 'labor',
      status: laborStatus,
      icon: 'alert',
      title: laborStatus === 'good' 
        ? 'Labor is on target this week.'
        : 'Labor is trending above target this week.',
      description: laborStatus === 'good'
        ? `At ${laborPercent.toFixed(1)}% of sales, labor costs are healthy.`
        : `If this continues, weekly cash tightens by approximately $${Math.abs(laborExpenses - (currentGrossSales * 0.30)).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.`,
      recommendation: generateRecommendation('labor', laborStatus, laborPercent, laborPercent - prevLaborPercent),
    },
    {
      id: 'foodCost',
      status: foodStatus,
      icon: 'eye',
      title: foodStatus === 'good'
        ? `Food cost improved by ${Math.abs(foodPercent - prevFoodPercent).toFixed(0)}% compared to last week.`
        : `Food cost is ${foodPercent > prevFoodPercent ? 'up' : 'stable'} this week.`,
      description: foodStatus === 'good'
        ? `This contributes approximately $${Math.abs((prevFoodPercent - foodPercent) / 100 * currentGrossSales).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")} in additional margin.`
        : `Food costs at ${foodPercent.toFixed(1)}% of sales.`,
      recommendation: generateRecommendation('foodCost', foodStatus, foodPercent, foodPercent - prevFoodPercent),
    },
    {
      id: 'inventory',
      status: inventoryStatus,
      icon: 'lightbulb',
      title: inventoryStatus === 'action'
        ? 'Inventory spending exceeded target last week.'
        : inventoryStatus === 'attention'
        ? 'Inventory spending is approaching target.'
        : 'Inventory spending is on track.',
      description: inventoryStatus === 'action'
        ? 'This will cause a cash problem if the pattern continues.'
        : inventoryStatus === 'attention'
        ? 'Monitor ordering closely this week.'
        : 'Ordering is aligned with sales volume.',
      recommendation: generateRecommendation('inventory', inventoryStatus, avgWeeklyInventory, avgWeeklyInventory - prevFoodExpenses),
    },
  ];

  // Overall status
  const statusCounts = priorityDecisions.reduce(
    (acc, d) => {
      acc[d.status]++;
      return acc;
    },
    { good: 0, attention: 0, action: 0 }
  );
  
  let overallStatus: StatusLevel = 'good';
  let headline = 'This week looks good.';
  let subheadline = 'All key metrics are on track.';
  
  if (statusCounts.action > 0) {
    overallStatus = 'action';
    headline = 'This week needs attention.';
    subheadline = 'Driven by cash flow timing, labor performance, and sales mix.';
  } else if (statusCounts.attention > 0) {
    overallStatus = 'attention';
    headline = 'This week needs attention.';
    subheadline = 'Driven by cash flow timing, labor performance, and sales mix.';
  }

  // Monthly expenses calculation
  const bySubcategory = new Map<string, { total: number; days: number[] }>();
  const nonFixedSubcategories = new Set(['payroll', 'labor', 'sales_tax', 'payroll_tax', 'cogs', 'food_cost', 'food']);
  recurringExpenses.forEach((tx: { amount?: number; subcategory?: string | null; transaction_date?: string }) => {
    const sub = (tx.subcategory || '').toLowerCase().trim().replace(/\s+/g, '_');
    if (nonFixedSubcategories.has(sub)) return;
    const amount = Math.abs(Number(tx.amount) || 0);
    if (!amount) return;
    const key = sub || 'other';
    const existing = bySubcategory.get(key) || { total: 0, days: [] };
    existing.total += amount;
    if (tx.transaction_date) existing.days.push(new Date(tx.transaction_date).getDate());
    bySubcategory.set(key, existing);
  });

  const monthlyExpensesFromBank: MonthlyExpense[] = [];
  const rentData = bySubcategory.get('rent');
  if (rentData) {
    const numMonths = Math.max(1, Math.ceil(recurringExpenses.filter((t: { subcategory?: string | null }) => (t.subcategory || '').toLowerCase() === 'rent').length / 2));
    const avgRent = rentData.total / numMonths;
    const typicalDay = rentData.days.length ? Math.round(rentData.days.reduce((a, b) => a + b, 0) / rentData.days.length) : 1;
    const dueStr = typicalDay <= 5 ? '1st of month' : typicalDay <= 15 ? '15th of month' : 'End of month';
    monthlyExpensesFromBank.push({ name: 'Rent', dueDate: dueStr, amount: Math.round(avgRent * 100) / 100 });
  }

  const opexSubs = ['opex', 'utilities', 'insurance', 'marketing', 'software', 'subscriptions', 'other'];
  let opexSum = 0;
  opexSubs.forEach(sub => {
    const data = bySubcategory.get(sub);
    if (data) opexSum += data.total;
  });
  if (opexSum > 0) {
    const opexTxCount = recurringExpenses.filter((t: { subcategory?: string | null }) => {
      const s = (t.subcategory || '').toLowerCase().trim().replace(/\s+/g, '_');
      return s && !nonFixedSubcategories.has(s) && s !== 'rent';
    }).length;
    const numMonths = Math.max(1, Math.ceil(opexTxCount / 2));
    const avgOpex = opexSum / numMonths;
    monthlyExpensesFromBank.push({ name: 'Monthly OPEX', dueDate: 'Recurring', amount: Math.round(avgOpex * 100) / 100 });
  }

  monthlyExpensesFromBank.push({ name: 'Sales Tax', dueDate: '20th of month' });
  monthlyExpensesFromBank.push({ name: 'Payroll Tax', dueDate: '15th of month' });

  const monthlyExpenses: MonthlyExpense[] = monthlyExpensesFromBank.length >= 2
    ? monthlyExpensesFromBank
    : [
        { name: 'Rent', dueDate: '1st of month' },
        { name: 'Sales Tax', dueDate: '20th of month' },
        { name: 'Payroll Tax', dueDate: '15th of month' },
        { name: 'Monthly OPEX', dueDate: 'End of month' },
      ];

  // Cash position: prefer live Teller balances, fall back to persisted current_balance
  const currentBalance = bankAccounts.reduce((sum, acc) => {
    if (acc.id in liveTellerBalances) {
      return sum + liveTellerBalances[acc.id];
    }
    return sum + (Number((acc as Record<string, unknown>).current_balance) || 0);
  }, 0);
  const monthlyFixedTotal = monthlyExpenses.reduce((sum, e) => sum + (e.amount ?? 0), 0);
  const weeklyAllocationFixedExpenses = monthlyFixedTotal / 4;
  const safeToSpend = Math.max(
    0,
    currentBalance - salesTaxLiability - payrollTaxLiability - weeklyAllocationFixedExpenses
  );

  // Enriched platform sales with per-platform KPIs per KPI Map v1.0
  const cogsRatio = currentGrossSalesExclTax > 0 ? foodExpenses / currentGrossSalesExclTax : 0;

  const platformSales: PlatformSales[] = [
    {
      platform: 'In-Store',
      grossSales: round2(toastGrossSalesExclTax),
      netSales: round2(toastGrossSalesExclTax),
      grossProfit: round2(toastGrossSalesExclTax - toastTransactionFees - (toastGrossSalesExclTax * cogsRatio)),
      tickets: toastTickets,
      avgTicketPrice: round2(toastATP),
      tipsCollected: round2(toastTips),
      salesTaxCollected: round2(toastSalesTax),
      commissions: 0,
      commissionPercent: 0,
      transactionFees: round2(toastTransactionFees),
      adSpend: 0,
      promotions: 0,
      netPayouts: round2(toastNetPayouts),
      payoutRatio: round2(toastPayoutRatio * 100) / 100,
    },
    {
      platform: 'DoorDash',
      grossSales: round2(ddGrossSales),
      netSales: round2(ddNetSales),
      grossProfit: round2(ddNetSales - ddCommissions - ddAdSpend - (ddNetSales * cogsRatio)),
      tickets: ddTickets,
      avgTicketPrice: round2(ddATP),
      tipsCollected: round2(ddTips),
      salesTaxCollected: round2(ddSalesTax),
      commissions: round2(ddCommissions),
      commissionPercent: round2(ddCommissionPct),
      transactionFees: 0,
      adSpend: round2(ddAdSpend),
      promotions: round2(ddDeductions),
      netPayouts: round2(ddNetPayouts),
      payoutRatio: round2(ddPayoutRatio * 100) / 100,
    },
    {
      platform: 'UberEats',
      grossSales: round2(uberGrossSales),
      netSales: round2(uberNetSales),
      grossProfit: round2(uberNetSales - uberCommissions - uberAdSpend - (uberNetSales * cogsRatio)),
      tickets: uberTickets,
      avgTicketPrice: round2(uberATP),
      tipsCollected: round2(uberTips),
      salesTaxCollected: round2(uberSalesTax),
      commissions: round2(uberCommissions),
      commissionPercent: round2(uberCommissionPct),
      transactionFees: 0,
      adSpend: round2(uberAdSpend),
      promotions: round2(uberDeductions),
      netPayouts: round2(uberNetPayouts),
      payoutRatio: round2(uberPayoutRatio * 100) / 100,
    },
  ];

  // ── Macro KPIs (Section 4 of KPI Map) ──
  const totalDeposits = depositsTxns.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const depositsMinusTax = totalDeposits - totalSalesTaxCollected;
  const totalExpenses = laborExpenses + foodExpenses + rentExpenses + opexExpenses;
  const debtService = debtServiceTxns.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
  const incomeTax = incomeTaxTxns.reduce((s, t) => s + Math.abs(Number(t.amount) || 0), 0);
  const noi = depositsMinusTax - totalExpenses;
  const netProfit = noi - debtService - incomeTax;

  const expenseRatios: ExpenseRatios = {
    payrollPercentGross: currentGrossSalesExclTax > 0 ? (laborExpenses / currentGrossSalesExclTax) * 100 : 0,
    payrollPercentNet: currentNetSalesExclTax > 0 ? (laborExpenses / currentNetSalesExclTax) * 100 : 0,
    rentPercentGross: currentGrossSalesExclTax > 0 ? (rentExpenses / currentGrossSalesExclTax) * 100 : 0,
    rentPercentNet: currentNetSalesExclTax > 0 ? (rentExpenses / currentNetSalesExclTax) * 100 : 0,
    cogsPercentGross: currentGrossSalesExclTax > 0 ? (foodExpenses / currentGrossSalesExclTax) * 100 : 0,
    cogsPercentNet: currentNetSalesExclTax > 0 ? (foodExpenses / currentNetSalesExclTax) * 100 : 0,
    opexPercentGross: currentGrossSalesExclTax > 0 ? (opexExpenses / currentGrossSalesExclTax) * 100 : 0,
    opexPercentNet: currentNetSalesExclTax > 0 ? (opexExpenses / currentNetSalesExclTax) * 100 : 0,
  };

  const macroKPIs: MacroKPIs = {
    grossSales: round2(currentGrossSalesExclTax),
    netSales: round2(currentNetSalesExclTax),
    tipsCollected: round2(totalTips),
    salesTaxCollected: round2(totalSalesTaxCollected),
    totalPayouts: round2(totalNetPayouts),
    totalExpenses: round2(totalExpenses),
    payroll: round2(laborExpenses),
    rent: round2(rentExpenses),
    cogs: round2(foodExpenses),
    opex: round2(opexExpenses),
    expenseRatios,
    noi: round2(noi),
    debtService: round2(debtService),
    incomeTax: round2(incomeTax),
    netProfit: round2(netProfit),
    tickets: totalTickets,
    avgTicketPrice: round2(overallATP),
  };

  // Performance snapshot
  const sellChange = previousGrossSales > 0 
    ? ((currentGrossSalesExclTax - previousGrossSales) / previousGrossSales) * 100 
    : 0;

  // Previous period net profit for proper period-over-period comparison
  const prevTotalExpenses = previousWeekExpenses.reduce((sum, e) => sum + Math.abs(Number(e.amount) || 0), 0);
  const prevNetProfit = previousNetPayout - prevTotalExpenses;
  const makeChange = Math.abs(prevNetProfit) > 0
    ? ((netProfit - prevNetProfit) / Math.abs(prevNetProfit)) * 100
    : 0;

  const comparisonText = dateRange?.startDate && dateRange?.endDate
    ? `Compared to previous period (${format(previousStart, 'MMM d')} – ${format(previousEnd, 'MMM d, yyyy')}).`
    : 'Compared to last week at the same point in time.';

  const performanceSnapshot: PerformanceSnapshot = {
    whatDidISell: round2(currentGrossSalesExclTax),
    whatDidIMake: round2(netProfit),
    sellChange: round2(sellChange),
    makeChange: round2(makeChange),
    comparisonText,
    insight: sellChange < 0 
      ? 'Sales are slightly below expectation, but margins remain stable.'
      : 'Sales are tracking well against last week.',
  };

  // Spending breakdown (includes dollar amounts alongside percentages)
  const spendingBreakdown: SpendingBreakdown = {
    labor: { percent: laborPercent, amount: laborExpenses, target: 30 },
    foodAndPackaging: { percent: foodPercent, amount: foodExpenses, target: 30 },
    operationalExpenses: { percent: opexPercent, amount: opexExpenses },
    nonBusiness: { percent: nonBusinessPercent, amount: nonBusinessExpenses },
  };

  // Shift performance: read from shift_performance table, fall back to on-the-fly
  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  let topShifts: ShiftPerformance[] = [];
  let bottomShifts: ShiftPerformance[] = [];

  const { data: shiftRowsRaw } = await (supabase as any)
    .from('shift_performance')
    .select('date, shift_period, gross_sales')
    .eq('company_id', companyId)
    .gte('date', currentStartStr)
    .lte('date', currentEndStr);

  const shiftRows = (shiftRowsRaw ?? []) as { date: string; shift_period: string; gross_sales: number }[];

  if (shiftRows.length > 0) {
    const shiftRevenueMap = new Map<string, number>();
    for (const row of shiftRows) {
      const dateStr = row.date;
      const gross = Number(row.gross_sales) || 0;
      if (!dateStr || gross <= 0) continue;
      const date = parseISO(dateStr);
      const dayName = DAY_NAMES[getDay(date)];
      const period = SHIFT_LABELS[row.shift_period as keyof typeof SHIFT_LABELS] ?? 'All day';
      const label = `${dayName} ${period}`;
      shiftRevenueMap.set(label, (shiftRevenueMap.get(label) ?? 0) + gross);
    }
    const sortedShifts = Array.from(shiftRevenueMap.entries())
      .map(([shift, revenue]) => ({ shift, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
    topShifts = sortedShifts.slice(0, 3);
    bottomShifts = sortedShifts.length <= 3 ? [] : sortedShifts.slice(-3).reverse();
  } else {
    // Fallback: compute from sales_transactions using order_time OR sale_time
    const shiftRevenueMap = new Map<string, number>();
    for (const row of currentWeekSales as { sale_date?: string | null; sale_time?: string | null; order_time?: string | null; gross_sales?: number | null }[]) {
      const dateStr = row.sale_date;
      const gross = Number(row.gross_sales) || 0;
      if (!dateStr || gross <= 0) continue;
      const date = parseISO(dateStr);
      const dayName = DAY_NAMES[getDay(date)];
      const timeStr = (row as any).order_time ?? row.sale_time ?? null;
      const shift = getShiftPeriod(timeStr);
      const period = shift ? SHIFT_LABELS[shift] : 'All day';
      const label = period === 'All day' ? dayName : `${dayName} ${period}`;
      shiftRevenueMap.set(label, (shiftRevenueMap.get(label) ?? 0) + gross);
    }
    const sortedShifts = Array.from(shiftRevenueMap.entries())
      .map(([shift, revenue]) => ({ shift, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
    topShifts = sortedShifts.slice(0, 3);
    bottomShifts = sortedShifts.length <= 3 ? [] : sortedShifts.slice(-3).reverse();
  }

  return {
    overallStatus,
    headline,
    subheadline,
    priorityDecisions,
    cashPosition: {
      currentBalance,
      safeToSpend,
      inventorySpendTarget,
    },
    taxLiabilities,
    monthlyExpenses,
    spendingBreakdown,
    performanceSnapshot,
    platformSales,
    topShifts,
    bottomShifts,
    macroKPIs,
  };
}
